import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const containerStyle = { maxWidth: 1000, margin: '40px auto', padding: 24, border: '1px solid #ddd', borderRadius: 10, background: '#fafafa' };
const btnSecondary = { padding: '10px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' };

const API_BASE = process.env.REACT_APP_API_BASE || '/api';

// ✅ Reusable image component
function QuestionImage({ src }) {
  if (!src) return null;
  
  return (
    <div style={{ 
      marginTop: 10, 
      marginBottom: 10,
      padding: 8,
      background: '#f8f9fa',
      borderRadius: 6,
      border: '1px solid #e9ecef'
    }}>
      <img 
        src={src} 
        alt="Question illustration" 
        style={{ 
          maxWidth: '100%', 
          maxHeight: 200,
          display: 'block',
          borderRadius: 4
        }}
        onError={(e) => {
          e.target.style.display = 'none';
          if(e.target.nextSibling) e.target.nextSibling.style.display = 'block';
        }}
      />
      <div style={{ 
        display: 'none', 
        padding: 8, 
        background: '#fff3cd', 
        color: '#856404', 
        borderRadius: 4, 
        fontSize: 12,
        marginTop: 4
      }}>
        ⚠️ Image failed to load: {src}
      </div>
    </div>
  );
}

export default function FlowExamPreview() {
  const { examId } = useParams();
  const navigate = useNavigate();
  
  const [exam, setExam] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeChapter, setActiveChapter] = useState(null);

  useEffect(() => {
    if (examId) fetchPreview();
  }, [examId]);

  const fetchPreview = async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/admin/exams/${examId}/diagnostic-questions`);
      if (!res.ok) throw new Error('Failed to fetch questions');
      const qData = await res.json();
      
      setExam(qData.exam);
      setData(qData);
      
      const chapters = Object.keys(qData.groupedByChapter || {});
      if (chapters.length > 0) setActiveChapter(chapters[0]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const currentChapterData = data?.groupedByChapter?.[activeChapter] || { corePairs: [], supportingPerfect: [], supportingFoundation: [] };

  if (loading) return <div style={{ textAlign: 'center', marginTop: 60 }}>⏳ Loading exam preview...</div>;
  if (error) return <div style={{ padding: 20, background: '#f8d7da', color: '#721c24', borderRadius: 8 }}>⚠️ {error}<br/><button onClick={() => navigate(-1)} style={{...btnSecondary, marginTop: 10}}>← Back</button></div>;
  if (!exam || !data) return <div style={{ textAlign: 'center', marginTop: 60 }}>Exam data not available.</div>;

  // ✅ Sort supporting questions by difficulty
  const sortQuestionsByDifficulty = (questions) => {
    const difficultyOrder = { 'basic': 1, 'intermediate': 2, 'advanced': 3, 'supporting': 4, 'core': 5 };
    return [...questions].sort((a, b) => {
      const orderA = difficultyOrder[a.difficulty?.toLowerCase()] || 99;
      const orderB = difficultyOrder[b.difficulty?.toLowerCase()] || 99;
      return orderA - orderB;
    });
  };

  const sortedSupportingPerfect = sortQuestionsByDifficulty(currentChapterData.supportingPerfect);
  const isScience = exam.topic === 'science';

  return (
    <div style={containerStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>
          {isScience ? '🔬 Science Diagnostic Preview' : '📐 Math Diagnostic Preview'}: {exam.name}
        </h2>
        <button onClick={() => navigate('/admin/flow-builder-exams')} style={btnSecondary}>← Back to Exams</button>
      </div>

      <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd', marginBottom: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
          <div><strong>Level:</strong> {exam.level}</div>
          <div><strong>Chapters:</strong> {exam.config?.chapters?.map(c => c.chapter_name).join(', ') || 'None'}</div>
          
          {isScience ? (
            <>
              <div><strong>Total Concepts:</strong> {exam.config?.chapters?.reduce((acc, ch) => acc + (ch.concepts?.length || 0), 0) || 0}</div>
              <div><strong>Total Sub-Concepts:</strong> {exam.config?.chapters?.reduce((acc, ch) => acc + ch.concepts?.reduce((a, c) => a + (c.sub_concepts?.length || 0), 0), 0) || 0}</div>
            </>
          ) : (
            <>
              <div><strong>Core Pass Threshold:</strong> {exam.config?.thresholds?.core_pass}%</div>
              <div><strong>Core Fail Threshold:</strong> {exam.config?.thresholds?.core_fail}%</div>
            </>
          )}
        </div>
      </div>

      {/* Chapter Tabs */}
      {Object.keys(data.groupedByChapter || {}).length > 0 ? (
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
          {Object.keys(data.groupedByChapter).map(chapterName => (
            <button
              key={chapterName}
              onClick={() => setActiveChapter(chapterName)}
              style={{
                padding: '8px 12px',
                background: activeChapter === chapterName ? '#007BFF' : '#e9ecef',
                color: activeChapter === chapterName ? '#fff' : '#333',
                border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13,
                fontWeight: activeChapter === chapterName ? 600 : 400
              }}
            >
              {chapterName}
            </button>
          ))}
        </div>
      ) : null}

      {/* Questions Sections */}
      {activeChapter && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 25 }}>
          
          {/* 1. Core Pairs */}
          <div>
            <h4 style={{ margin: '0 0 10px 0', color: '#0056b3' }}>🔗 Primary → Backup Mapping (Core)</h4>
            {currentChapterData.corePairs.length > 0 ? (
              currentChapterData.corePairs.map((pair, idx) => (
                <div key={pair.primary.id} style={{ background: '#fff', border: '1px solid #dee2e6', borderRadius: 8, overflow: 'hidden', marginBottom: 10 }}>
                  <div style={{ padding: '12px 15px', background: '#e7f1ff', borderBottom: '1px solid #dee2e6' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <strong style={{ color: '#0056b3', fontSize: 16 }}>Primary Q{idx + 1}</strong>
                      <span style={{ padding: '2px 8px', background: pair.primary.difficulty?.toLowerCase() === 'basic' ? '#28a745' : (pair.primary.difficulty?.toLowerCase() === 'core' ? '#6f42c1' : '#fd7e14'), color: '#fff', borderRadius: 4, fontSize: 11, fontWeight: 600, textTransform: 'uppercase' }}>
                        {pair.primary.difficulty}
                      </span>
                    </div>
                    <p style={{ margin: '4px 0', fontSize: 14 }}>{pair.primary.text}</p>
                    <div style={{ fontSize: 12, color: '#666' }}>Concept: {pair.primary.concept}</div>
                    
                    <QuestionImage src={pair.primary.image_url} />
                  </div>
                  <div style={{ textAlign: 'center', padding: '6px 0', background: '#f8f9fa', color: '#666', fontSize: 12 }}>
                    If Wrong ⬇️
                  </div>
                  <div style={{ padding: '12px 15px', background: pair.backups && pair.backups.length > 0 ? '#fff8e1' : '#fff' }}>
                    {pair.backups && pair.backups.length > 0 ? (
                      pair.backups.map((backup, bIdx) => (
                        <div key={backup.id || bIdx} style={{ marginBottom: bIdx < pair.backups.length - 1 ? 15 : 0, paddingBottom: bIdx < pair.backups.length - 1 ? 15 : 0, borderBottom: bIdx < pair.backups.length - 1 ? '1px dashed #ffc107' : 'none' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <strong style={{ color: '#856404', fontSize: 16 }}>Locked Backup {bIdx + 1}</strong>
                            <span style={{ padding: '2px 8px', background: backup.difficulty?.toLowerCase() === 'basic' ? '#28a745' : '#fd7e14', color: '#fff', borderRadius: 4, fontSize: 11, fontWeight: 600, textTransform: 'uppercase' }}>
                              BACKUP ({backup.difficulty})
                            </span>
                          </div>
                          <p style={{ margin: '4px 0', fontSize: 14 }}>{backup.text}</p>
                          <div style={{ fontSize: 12, color: '#666' }}>Concept: {backup.concept}</div>
                          <QuestionImage src={backup.image_url} />
                        </div>
                      ))
                    ) : (
                      <p style={{ margin: 0, color: '#999', fontStyle: 'italic' }}>
                        {isScience 
                          ? 'Backup questions are dynamically selected per sub-concept during the exam based on admin configuration.' 
                          : 'No backup configured for this concept.'}
                      </p>
                    )}
                  </div>
                </div>
              ))
            ) : <p style={{ color: '#666', fontStyle: 'italic' }}>No Core pairs mapped.</p>}
          </div>

          {/* 2. Perfect Score Path */}
          <div>
            <h4 style={{ margin: '0 0 10px 0', color: '#28a745' }}>🎯 Perfect Score Path (Supporting)</h4>
            {sortedSupportingPerfect.length > 0 ? (
              sortedSupportingPerfect.map((q, idx) => (
                <div key={q.id} style={{ padding: '12px 15px', background: '#fff', border: '1px solid #dee2e6', borderRadius: 8, marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <strong style={{ fontSize: 16 }}>Q{idx + 1}</strong>
                    <span style={{ 
                      padding: '2px 8px', 
                      background: q.difficulty?.toLowerCase() === 'intermediate' ? '#fd7e14' : 
                                 q.difficulty?.toLowerCase() === 'advanced' ? '#6f42c1' : 
                                 q.difficulty?.toLowerCase() === 'supporting' ? '#28a745' : '#28a745', 
                      color: '#fff', 
                      borderRadius: 4, 
                      fontSize: 11, 
                      fontWeight: 600,
                      textTransform: 'uppercase'
                    }}>
                      {q.difficulty}
                    </span>
                  </div>
                  <p style={{ margin: '4px 0', fontSize: 14 }}>{q.text}</p>
                  <div style={{ fontSize: 12, color: '#666' }}>Concept: {q.concept}</div>
                  
                  <QuestionImage src={q.image_url} />
                </div>
              ))
            ) : <p style={{ color: '#666', fontStyle: 'italic' }}>No Perfect Path questions.</p>}
          </div>

          {/* 3. Foundation Track */}
          <div>
            <h4 style={{ margin: '0 0 10px 0', color: '#dc3545' }}>🌱 Foundation Track (Supporting Basic)</h4>
            {currentChapterData.supportingFoundation.length > 0 ? (
              currentChapterData.supportingFoundation.map((q, idx) => (
                <div key={q.id} style={{ padding: '10px 15px', background: '#fff', border: '1px solid #dee2e6', borderRadius: 6, marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <strong>Q{idx + 1}</strong>
                    <span style={{ padding: '2px 6px', background: '#28a745', color: '#fff', borderRadius: 4, fontSize: 10, fontWeight: 600 }}>BASIC</span>
                  </div>
                  <p style={{ margin: '4px 0', fontSize: 14 }}>{q.text}</p>
                  <div style={{ fontSize: 11, color: '#666' }}>Concept: {q.concept}</div>
                  
                  <QuestionImage src={q.image_url} />
                </div>
              ))
            ) : <p style={{ color: '#666', fontStyle: 'italic' }}>No Foundation questions.</p>}
          </div>

        </div>
      )}

      <div style={{ marginTop: 25, padding: 12, background: '#d4edda', borderRadius: 6, fontSize: 13 }}>
        ✅ <strong>Locked Mapping Complete:</strong> Primary questions are shown with their permanently locked backup questions (or dynamic config for Science). Supporting questions are shown for Perfect Path & Foundation tracks.
      </div>

      <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={() => navigate('/admin/flow-builder-exams')} style={btnSecondary}>← Back to Exams</button>
      </div>
    </div>
  );
}